import PropTypes from 'prop-types'
import InformationCircle from '@/assets/svgs/information-circle.svg?react'
import StatusBanner from '@/components/StatusBanner/StatusBanner'
import ActivityLogModal from '@/components/ActivityLogModal'
import useModal from '@/hooks/useModal'
import { formatDate } from '@/utils/formatDate'
import {
  DEFAULT_SECONDARY_LABEL,
  FIRST_APPROVER,
  SUBMITTAL_STATUS,
  submissionNoun,
  withOrg,
} from '../constants'
import { buildLogEntries } from '../utils/buildLogEntries'

// Reads like SUBMITTAL_STATUS_TAGS, and deliberately so: the banner sits directly
// under the tag, so the two have to agree about the same submittal. Each entry's
// `whenSecondaryRequired` replaces the presentation on an element that has a
// second approver, where the one-stage sentence would be wrong rather than merely
// vague. `{org}` is the second approver's name, substituted by withOrg over the
// finished title.
//
// Single-stage copy is pitstop's, word for word (src/pages/submittals/
// components/SubmittalBanner.jsx): the same submittal is read by the submitter
// there and the consultant here, and a sentence that differs between the two
// reads as two different events. The two-stage wording below is this app's own —
// a submitter reading pitstop is not the audience for a page that only a
// consultant or {org} ever opens — so it is free to name the actor plainly
// rather than match pitstop's phrasing.
const BANNER_CONFIG = {
  [SUBMITTAL_STATUS.DRAFT]: {
    getPrimary: () => 'Submission is saved as draft',
  },
  [SUBMITTAL_STATUS.PENDING_REVIEW]: {
    getPrimary: () => 'Submission is in pending review',
    // Two stages means "pending review" alone does not say which one, and the
    // status tag above distinguishes them the same way.
    whenSecondaryRequired: {
      getPrimary: () => 'Submission is in pending consultant review.',
    },
  },
  [SUBMITTAL_STATUS.APPROVED]: {
    getPrimary: ({ receiverOrgName, submissionMode }) =>
      `${submissionNoun(submissionMode)} was approved and shared to ${receiverOrgName || 'receiver'}`,
    showBy: true,
    // There is no separate awaiting-second-approval status: on a two-stage
    // element APPROVED means the consultant is done and the second approver has
    // not started. The single-stage copy is wrong here — it claims a share to the
    // receiver that does not happen until the second approval.
    //
    // Names where it now sits rather than who acted: the status tag beside it
    // already reads "Pending {org} Review", so the banner backs that up instead
    // of repeating the consultant's own approval back to them.
    whenSecondaryRequired: {
      getPrimary: () => 'Submission has been sent to {org} and is awaiting their review.',
    },
  },
  [SUBMITTAL_STATUS.REJECTED]: {
    getPrimary: () => 'Submission was rejected',
    showReasonCard: true,
    // Two-stage only: naming the consultant here would be redundant on a
    // single-stage element, where they are the only reviewer there is. The
    // second approver's own rejection is SECONDARY_REJECTED, not this status.
    whenSecondaryRequired: {
      getPrimary: () => `Submission has been rejected by the ${FIRST_APPROVER}.`,
    },
  },
  [SUBMITTAL_STATUS.REPLACED]: {
    getPrimary: () => 'Submission has been replaced by a newer submission',
    showReasonCard: true,
  },
  [SUBMITTAL_STATUS.DELETED]: {
    getPrimary: () => 'Submission has been deleted',
  },
  [SUBMITTAL_STATUS.PASSED_DEADLINE]: {
    getPrimary: () => 'Submission deadline has passed',
  },

  // Second-stage outcomes. Never keyed off `required` — reaching one of these
  // already implies a second approval. Without them the banner renders nothing
  // once the second approver acts, which also takes the only link to the activity
  // log off the page. `{org}` is the approver who acted, read from the element's
  // own label the same way the SECONDARY_APPROVED/SECONDARY_REJECTED tags are.
  [SUBMITTAL_STATUS.SECONDARY_APPROVED]: {
    getPrimary: () => 'Submission has been approved by {org}.',
    showBy: true,
  },
  [SUBMITTAL_STATUS.SECONDARY_REJECTED]: {
    getPrimary: () => 'Submission has been rejected by {org}.',
    showReasonCard: true,
  },
}

/**
 * A submittal's status as a banner, with its activity log behind it.
 *
 * The log is the banner's own concern rather than the page's: the trigger only
 * exists here, the entries come from the same logs the banner already reads, and
 * every caller was otherwise repeating the same five lines of modal plumbing —
 * a `useModal`, the empty-log gate, the entry mapping and the modal itself.
 *
 * `activityLogs` is newest-first, as `GET /submittals/{id}/activity` returns it,
 * so the banner's own text comes from entry 0.
 *
 * `centerAction` is presentation only, and the caller's call rather than this
 * component's: the same banner draws on the EMS detail page, which keeps its
 * existing top-aligned trigger.
 */
export const SubmittalBanner = ({
  status,
  receiverOrgName,
  submissionMode,
  secondaryApproval,
  activityLogs = [],
  centerAction = false,
  compactDates = false,
}) => {
  const { visible, show, hide } = useModal()

  const latestLog = activityLogs[0] ?? null
  const base = latestLog ? BANNER_CONFIG[status] : null
  // Hooks run first, so the early return has to come after useModal.
  if (!base) return null

  const config =
    secondaryApproval?.required && base.whenSecondaryRequired
      ? { ...base, ...base.whenSecondaryRequired }
      : base

  const secondaryLabel = secondaryApproval?.label || DEFAULT_SECONDARY_LABEL

  // A second-stage entry's actor is a pitstop user, not a consultant, so it
  // arrives under `user` — without that fallback the "by" line vanishes on exactly
  // the entries the second approver wrote, which is where SECONDARY_APPROVED's
  // `showBy` is meant to show it.
  const actorLabel =
    latestLog.consultant?.email ?? latestLog.consultant?.name ?? latestLog.user?.email
  const subtitle = [
    config.showBy && actorLabel ? `by ${actorLabel}` : null,
    // Padded hour unless the caller opts out, so the banner and the header above
    // it agree, and so EMS keeps the timestamp it renders in production.
    latestLog.created ? `on ${formatDate(latestLog.created, { padHour: !compactDates })}` : null,
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <>
      <StatusBanner
        className="rounded-2xl mt-4 mb-4"
        icon={<InformationCircle />}
        // Substituted over the finished sentence, so a `{org}` anywhere in the
        // copy resolves — the same way pitstop applies it.
        title={withOrg(config.getPrimary({ receiverOrgName, submissionMode }), secondaryLabel)}
        subtitle={subtitle || undefined}
        centerAction={centerAction}
        action={
          <StatusBanner.Action onClick={show}>
            <span className="phone:hidden">View Log</span>
            <span className="hidden phone:inline">View Activity Log</span>
          </StatusBanner.Action>
        }
      >
        {config.showReasonCard && latestLog.message && (
          <StatusBanner.Note variant="error">
            <StatusBanner.Note.Heading>Reason for rejection:</StatusBanner.Note.Heading>
            <StatusBanner.Note.Body>{latestLog.message}</StatusBanner.Note.Body>
            {actorLabel && <StatusBanner.Note.Meta>From: {actorLabel}</StatusBanner.Note.Meta>}
          </StatusBanner.Note>
        )}
      </StatusBanner>
      <hr className="block w-full h-px mb-4 p-0 bg-grey-90 border-none box-border" />

      {/* The element's workflow reaches the timeline too, so a two-stage
          submittal's entries name their stage the way the banner and the status
          tag above already do. */}
      <ActivityLogModal
        visible={visible}
        close={hide}
        entries={buildLogEntries(activityLogs, secondaryApproval)}
      />
    </>
  )
}

SubmittalBanner.propTypes = {
  status: PropTypes.string,
  receiverOrgName: PropTypes.string,
  // Decides what the copy calls the submission. Absent means eform, which is
  // what EMS relies on to keep its existing wording.
  submissionMode: PropTypes.string,
  // The element's workflow, read as one concept though it is stored as two
  // attributes — same shape the status tag resolves against.
  secondaryApproval: PropTypes.shape({
    required: PropTypes.bool,
    label: PropTypes.string,
  }),
  // Newest first. The banner renders nothing at all when empty: with no log there
  // is neither a date to show nor anything for the modal to list.
  activityLogs: PropTypes.arrayOf(
    PropTypes.shape({
      created: PropTypes.string.isRequired,
      message: PropTypes.string,
      created_by: PropTypes.string,
      consultant: PropTypes.shape({ email: PropTypes.string, name: PropTypes.string }),
      // A second-stage entry's actor: a pitstop user rather than a consultant.
      user: PropTypes.shape({ email: PropTypes.string }),
    })
  ),
  centerAction: PropTypes.bool,
  // Drops the timestamp's leading zero, as pitstop renders it. Off by default,
  // which is what keeps EMS unchanged — same gate the header takes.
  compactDates: PropTypes.bool,
}
